import { useEffect, useId, useRef } from 'react';

declare global {
  interface Window {
    tinymce?: {
      init: (opts: Record<string, unknown>) => Promise<unknown> | void;
      get: (id: string) => { remove: () => void; getContent: () => string; setContent: (h: string) => void } | null;
      remove: (id?: string) => void;
    };
  }
}

const TINYMCE_CDN = 'https://cdn.jsdelivr.net/npm/tinymce@7.6.1/tinymce.min.js';

let tinymceLoader: Promise<void> | null = null;

function loadTinymce(): Promise<void> {
  if (window.tinymce) return Promise.resolve();
  if (tinymceLoader) return tinymceLoader;
  tinymceLoader = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${TINYMCE_CDN}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      if (window.tinymce) resolve();
      return;
    }
    const s = document.createElement('script');
    s.src = TINYMCE_CDN;
    s.referrerPolicy = 'origin';
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load TinyMCE'));
    document.head.appendChild(s);
  });
  return tinymceLoader;
}

type Props = {
  value: string;
  onChange: (html: string) => void;
  height?: number;
  placeholder?: string;
};

/**
 * Free TinyMCE (GPL) loaded from jsDelivr CDN — no cloud API key, keeps POS bundle small.
 */
export default function RichTextEditor({ value, onChange, height = 320, placeholder }: Props) {
  const id = useId().replace(/:/g, '');
  const editorId = `storefront-cms-${id}`;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        await loadTinymce();
        if (cancelled || !window.tinymce) return;

        // Remove prior instance if React remounted
        window.tinymce.get(editorId)?.remove();

        await window.tinymce.init({
          selector: `#${editorId}`,
          license_key: 'gpl',
          height,
          menubar: false,
          branding: false,
          promotion: false,
          placeholder: placeholder || 'Write page content…',
          plugins: 'lists link image table code autolink charmap',
          toolbar:
            'undo redo | blocks | bold italic underline | alignleft aligncenter alignright | bullist numlist | link image table | removeformat code',
          content_style:
            'body { font-family: Segoe UI, Helvetica Neue, Arial, sans-serif; font-size: 14px; line-height: 1.55; }',
          convert_urls: false,
          relative_urls: false,
          setup: (editor: {
            on: (ev: string, cb: () => void) => void;
            getContent: () => string;
            setContent: (h: string) => void;
          }) => {
            editor.on('init', () => {
              editor.setContent(valueRef.current || '');
            });
            editor.on('change keyup undo redo SetContent', () => {
              onChangeRef.current(editor.getContent());
            });
          },
        });
      } catch {
        /* textarea remains usable as fallback */
      }
    })();

    return () => {
      cancelled = true;
      try {
        window.tinymce?.get(editorId)?.remove();
      } catch {
        /* ignore */
      }
    };
  }, [editorId, height, placeholder]);

  // Keep editor in sync when switching between pages
  useEffect(() => {
    const ed = window.tinymce?.get(editorId);
    if (!ed) return;
    const current = ed.getContent();
    if ((value || '') !== current) {
      ed.setContent(value || '');
    }
  }, [value, editorId]);

  return (
    <div className="rich-text-editor border border-gray-200 rounded-xl overflow-hidden bg-white">
      <textarea
        id={editorId}
        className="input-field min-h-[180px] border-0 rounded-none"
        defaultValue={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || 'Write page content…'}
      />
    </div>
  );
}
